/**
 * 1.4 P27: admin — every customer import across owners, with run / retry /
 * bulk-resend (through the owner routes scoped by ?ownerId=) and the two
 * platform knobs (max rows per file, invite emails per minute).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useRouter, Stack } from 'expo-router';

import { api } from '../../src/api/client';
import { useTheme } from '../../src/context/ThemeContext';
import { useToast } from '../../src/components/Toast';
import ContentContainer from '../../src/components/ContentContainer';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import Input from '../../src/components/Input';
import { AdminToolbar } from '../../src/components/AdminList';
import type { ColorTheme } from '../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';

interface Imp { id: string; ownerId: string; ownerBusinessName: string; ownerEmail: string; status: string; filename: string | null; rowCount: number; okCount: number; warningCount: number; errorCount: number; createdCount: number; linkedCount: number; failedCount: number; billingMode: string | null; createdAt: string; error: string | null }

export default function AdminImportsScreen() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const router = useRouter();
  const toast = useToast();
  const [rows, setRows] = useState<Imp[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [maxRows, setMaxRows] = useState('5000');
  const [ratePerMin, setRatePerMin] = useState('50');
  const [savingKnobs, setSavingKnobs] = useState(false);

  const load = useCallback(async (p = page) => {
    try {
      const [list, knobs] = await Promise.all([
        api<{ imports: Imp[]; total: number }>(`/api/admin/imports?page=${p}&limit=25${search ? `&search=${encodeURIComponent(search)}` : ''}`),
        api<{ maxRows: number; inviteRatePerMin: number }>('/api/admin/import-settings').catch(() => null),
      ]);
      setRows(list.imports); setTotal(list.total); setPage(p);
      if (knobs) { setMaxRows(String(knobs.maxRows)); setRatePerMin(String(knobs.inviteRatePerMin)); }
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not load imports', message: err.message }); }
    finally { setLoading(false); }
  }, [page, search, toast]);
  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveKnobs = async () => {
    setSavingKnobs(true);
    try {
      await api('/api/admin/import-settings', { method: 'PUT', body: { maxRows: parseInt(maxRows) || 5000, inviteRatePerMin: parseInt(ratePerMin) || 50 } });
      toast.show({ type: 'success', title: 'Import limits saved' });
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not save', message: err.message }); }
    finally { setSavingKnobs(false); }
  };
  const retry = async (imp: Imp) => {
    setBusy(imp.id);
    try {
      await api(`/api/owner/imports/${imp.id}/commit?ownerId=${imp.ownerId}`, { method: 'POST', body: { billingMode: imp.billingMode ?? 'active_grace', sendInvites: true } });
      toast.show({ type: 'success', title: 'Import re-run started' }); load(page);
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not run', message: err.message }); }
    finally { setBusy(null); }
  };
  const resendAll = async (imp: Imp) => {
    setBusy(imp.id);
    try {
      const r = await api<{ sent: number }>(`/api/owner/imports/${imp.id}/resend-invites?ownerId=${imp.ownerId}`, { method: 'POST', body: {} });
      toast.show({ type: 'success', title: `Resent ${r.sent} invite${r.sent === 1 ? '' : 's'}` });
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not resend', message: err.message }); }
    finally { setBusy(null); }
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Customer Imports' }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={1100}>
          <Card style={styles.card}>
            <Text style={styles.title}>Import limits</Text>
            <View style={styles.row2}>
              <View style={{ flex: 1 }}><Input label="Max rows per file" value={maxRows} onChangeText={v => setMaxRows(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" /></View>
              <View style={{ flex: 1 }}><Input label="Invite emails per minute" value={ratePerMin} onChangeText={v => setRatePerMin(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" /></View>
            </View>
            <Button title="Save limits" size="sm" onPress={saveKnobs} loading={savingKnobs} style={{ alignSelf: 'flex-start' }} />
          </Card>
          <Card style={styles.card}>
            <Text style={styles.title}>All imports</Text>
            <AdminToolbar search={search} onSearchChange={setSearch} onSearch={() => load(1)} total={total} page={page} limit={25} onPageChange={p => load(p)} csvEndpoint={`/api/admin/imports${search ? `?search=${encodeURIComponent(search)}` : ''}`} csvFilename="customer-imports.csv" label="import" />
            {loading ? <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.lg }} /> : rows.length === 0 ? <Text style={styles.meta}>No imports yet.</Text> : rows.map(imp => (
              <View key={imp.id} style={styles.impRow}>
                <View style={{ flex: 1 }}>
                  <TouchableOpacity onPress={() => router.push(`/admin/owner/${imp.ownerId}/customers/imports/${imp.id}` as never)}>
                    <Text style={styles.impTitle}>{imp.ownerBusinessName} · {imp.filename || 'import'}</Text>
                  </TouchableOpacity>
                  <Text style={styles.meta}>{new Date(imp.createdAt).toLocaleString()} · {imp.ownerEmail} · {imp.rowCount} rows · {imp.okCount} ok / {imp.warningCount} warn / {imp.errorCount} err{imp.status !== 'validated' ? ` · ${imp.createdCount} created / ${imp.linkedCount} linked / ${imp.failedCount} failed` : ''}{imp.error ? ` · ${imp.error}` : ''}</Text>
                </View>
                <Text style={[styles.status, imp.status === 'done' && { color: colors.success }, imp.status === 'failed' && { color: colors.danger }]}>{imp.status}</Text>
                <View style={styles.actions}>
                  {imp.status === 'validated' && <Button title="Open" size="sm" variant="outline" onPress={() => router.push(`/admin/owner/${imp.ownerId}/customers/import` as never)} />}
                  {(imp.status === 'done' || imp.status === 'failed') && imp.failedCount > 0 && <Button title="Retry failed" size="sm" variant="outline" loading={busy === imp.id} onPress={() => retry(imp)} />}
                  {imp.status === 'done' && <Button title="Resend invites" size="sm" variant="outline" loading={busy === imp.id} onPress={() => resendAll(imp)} />}
                </View>
              </View>
            ))}
          </Card>
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  card: { marginBottom: spacing.md },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm },
  row2: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  impRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight, flexWrap: 'wrap' },
  impTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary },
  status: { width: 80, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, textAlign: 'center' },
  actions: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
});
